import { alignElements, Alignment } from "../align";
import { ToolButton } from "../components/ToolButton";
import {getNonDeletedElements, isTextElement} from "../element";
import { isFrameLikeElement } from "../element/typeChecks";
import { ExcalidrawElement } from "../element/types";
import { updateFrameMembershipOfSelectedElements } from "../frame";
import { t } from "../i18n";
import { KEYS } from "../keys";
import { isSomeElementSelected } from "../scene";
import { AppClassProperties, AppState } from "../types";
import { arrayToMap, getShortcutKey } from "../utils";
import { register } from "./register";
import {imgPathPerfix} from "../../../../src/utils";
import React from "react";
import { getCommonBounds } from "../element/bounds";

const alignActionsPredicate = (
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  _: unknown,
  app: AppClassProperties,
) => {
  const selectedElements = app.scene.getSelectedElements(appState);
  return (
    selectedElements.length > 1 &&
    // TODO enable aligning frames when implemented properly
    !selectedElements.some((el) => isFrameLikeElement(el))
  );
};

const alignSelectedElements = (
  elements: readonly ExcalidrawElement[],
  appState: Readonly<AppState>,
  app: AppClassProperties,
  alignment: Alignment,
) => {
  const selectedElements = app.scene.getSelectedElements(appState);

  const updatedElements = alignElements(selectedElements, alignment);

  const updatedElementsMap = arrayToMap(updatedElements);
  if (alignment.position == "horizontal" || alignment.position == 'vertical' || alignment.position == 'grid'){
    return {
      elements: updateFrameMembershipOfSelectedElements(
          elements.map((element) => updatedElementsMap.get(element.id) || element),
          appState,
          app,
      ),
      updatedElements, // Return the updated elements for scrolling
    };
  }
  return updateFrameMembershipOfSelectedElements(
        elements.map((element) => updatedElementsMap.get(element.id) || element),
        appState,
        app,
    )
};

export const actionAlignTop = register({
  name: "alignTop",
  trackEvent: { category: "element" },
  predicate: alignActionsPredicate,
  perform: (elements, appState, _, app) => {
    return {
      appState,
      elements: alignSelectedElements(elements, appState, app, {
        position: "start",
        axis: "y",
      }),
      align: true,
      commitToHistory: true,
    };
  },
  keyTest: (event) =>
    event[KEYS.CTRL_OR_CMD] && event.shiftKey && event.key === KEYS.ARROW_UP,
  PanelComponent: ({ elements, appState, updateData, app }) => (
    // <ToolButton
    //   hidden={!alignActionsPredicate(elements, appState, null, app)}
    //   type="button"
    //   icon={AlignTopIcon}
    //   onClick={() => updateData(null)}
    //   title={`${t("labels.alignTop")} — ${getShortcutKey(
    //     "CtrlOrCmd+Shift+Up",
    //   )}`}
    //   aria-label={t("labels.alignTop")}
    //   visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    // />
      <div className='flex-center border-radius-4-with-hover' style={{padding:'0px'}} onClick={()=>{
        updateData(null);
      }}>
        <img className='img-26' src={imgPathPerfix()+"mul_align_top.png"}/>
      </div>
  ),
});

export const actionAlignBottom = register({
  name: "alignBottom",
  trackEvent: { category: "element" },
  predicate: alignActionsPredicate,
  perform: (elements, appState, _, app) => {
    return {
      appState,
      elements: alignSelectedElements(elements, appState, app, {
        position: "end",
        axis: "y",
      }),
      align: true,
      commitToHistory: true,
    };
  },
  keyTest: (event) =>
    event[KEYS.CTRL_OR_CMD] && event.shiftKey && event.key === KEYS.ARROW_DOWN,
  PanelComponent: ({ elements, appState, updateData, app }) => (
    // <ToolButton
    //   hidden={!alignActionsPredicate(elements, appState, null, app)}
    //   type="button"
    //   icon={AlignBottomIcon}
    //   onClick={() => updateData(null)}
    //   title={`${t("labels.alignBottom")} — ${getShortcutKey(
    //     "CtrlOrCmd+Shift+Down",
    //   )}`}
    //   aria-label={t("labels.alignBottom")}
    //   visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    // />
      <div className='flex-center border-radius-4-with-hover' style={{padding:'0px'}} onClick={()=>{
        updateData(null);
      }}>
        <img className='img-26' src={imgPathPerfix()+"mul_align_v_bottom.png"}/>
      </div>
  ),
});

export const actionAlignLeft = register({
  name: "alignLeft",
  trackEvent: { category: "element" },
  predicate: alignActionsPredicate,
  perform: (elements, appState, _, app) => {
    return {
      appState,
      elements: alignSelectedElements(elements, appState, app, {
        position: "start",
        axis: "x",
      }),
      align: true,
      commitToHistory: true,
    };
  },
  keyTest: (event) =>
    event[KEYS.CTRL_OR_CMD] && event.shiftKey && event.key === KEYS.ARROW_LEFT,
  PanelComponent: ({ elements, appState, updateData, app }) => (
    // <ToolButton
    //   hidden={!alignActionsPredicate(elements, appState, null, app)}
    //   type="button"
    //   icon={AlignLeftIcon}
    //   onClick={() => updateData(null)}
    //   title={`${t("labels.alignLeft")} — ${getShortcutKey(
    //     "CtrlOrCmd+Shift+Left",
    //   )}`}
    //   aria-label={t("labels.alignLeft")}
    //   visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    // />
      <div className='flex-center border-radius-4-with-hover' style={{padding:'0px'}} onClick={()=>{
        updateData(null);
      }}>
        <img className='img-26' src={imgPathPerfix()+"mul_align_left.png"}/>
      </div>
  ),
});

export const actionAlignRight = register({
  name: "alignRight",
  trackEvent: { category: "element" },
  predicate: alignActionsPredicate,
  perform: (elements, appState, _, app) => {
    return {
      appState,
      elements: alignSelectedElements(elements, appState, app, {
        position: "end",
        axis: "x",
      }),
      align: true,
      commitToHistory: true,
    };
  },
  keyTest: (event) =>
    event[KEYS.CTRL_OR_CMD] && event.shiftKey && event.key === KEYS.ARROW_RIGHT,
  PanelComponent: ({ elements, appState, updateData, app }) => (
    // <ToolButton
    //   hidden={!alignActionsPredicate(elements, appState, null, app)}
    //   type="button"
    //   icon={AlignRightIcon}
    //   onClick={() => updateData(null)}
    //   title={`${t("labels.alignRight")} — ${getShortcutKey(
    //     "CtrlOrCmd+Shift+Right",
    //   )}`}
    //   aria-label={t("labels.alignRight")}
    //   visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    // />
      <div className='flex-center border-radius-4-with-hover' style={{padding:'0px'}} onClick={()=>{
        updateData(null);
      }}>
        <img className='img-26' src={imgPathPerfix()+"mul_align_right.png"}/>
      </div>
  ),
});

export const actionAlignVerticallyCentered = register({
  name: "alignVerticallyCentered",
  trackEvent: { category: "element" },
  predicate: alignActionsPredicate,
  perform: (elements, appState, _, app) => {
    return {
      appState,
      elements: alignSelectedElements(elements, appState, app, {
        position: "center",
        axis: "y",
      }),
      align: true,
      commitToHistory: true,
    };
  },
  PanelComponent: ({ elements, appState, updateData, app }) => (
    // <ToolButton
    //   hidden={!alignActionsPredicate(elements, appState, null, app)}
    //   type="button"
    //   icon={CenterVerticallyIcon}
    //   onClick={() => updateData(null)}
    //   title={t("labels.centerVertically")}
    //   aria-label={t("labels.centerVertically")}
    //   visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    // />
      <div className='flex-center border-radius-4-with-hover' style={{padding:'0px'}} onClick={()=>{
        updateData(null);
      }}>
        <img className='img-26' src={imgPathPerfix()+"mul_align_v_center.png"}/>
      </div>
  ),
});

export const actionAlignHorizontallyCentered = register({
  name: "alignHorizontallyCentered",
  trackEvent: { category: "element" },
  predicate: alignActionsPredicate,
  perform: (elements, appState, _, app) => {
    return {
      appState,
      elements: alignSelectedElements(elements, appState, app, {
        position: "center",
        axis: "x",
      }),
      align: true,
      commitToHistory: true,
    };
  },
  PanelComponent: ({ elements, appState, updateData, app }) => (
    // <ToolButton
    //   hidden={!alignActionsPredicate(elements, appState, null, app)}
    //   type="button"
    //   icon={CenterHorizontallyIcon}
    //   onClick={() => updateData(null)}
    //   title={t("labels.centerHorizontally")}
    //   aria-label={t("labels.centerHorizontally")}
    //   visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    // />
      <div className='flex-center border-radius-4-with-hover' style={{padding:'0px'}} onClick={()=>{
        updateData(null);
      }}>
        <img className='img-26' src={imgPathPerfix()+"mul_align_center.png"}/>
      </div>
  ),
});


export const actionAlignHorizontal = register({
  name: "alignHorizontal",
  trackEvent: { category: "element" },
  predicate: alignActionsPredicate,
  perform: (elements, appState, _, app) => {
    const result = alignSelectedElements(elements, appState, app, {
      position: "horizontal",
      axis: "arrange",
    });

    // // Scroll to content center after arrange
    // setTimeout(() => {
    //   app.scrollToContent(result.updatedElements, { animate: true, duration: 300 });
    // }, 0);
    return {
      appState: {
        ...appState,
        selectedElementIds: {},
      },
      elements: result.elements,
      commitToHistory: true,
      arrange: true,
    };
  },
  keyTest: (event) => false,
  PanelComponent: ({ elements, appState, updateData, app }) => (
      <ToolButton
          hidden={!alignActionsPredicate(elements, appState, null, app)}
          type="button"
          // icon={AlignHorizontalIcon}
          onClick={() => updateData(null)}
          title={`${t("labels.alignRight")} — ${getShortcutKey(
              "CtrlOrCmd+Shift+Right",
          )}`}
          aria-label={t("labels.alignRight")}
          visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
      />
  ),
});
export const actionAlignVertical = register({
  name: "alignVertical",
  trackEvent: { category: "element" },
  predicate: alignActionsPredicate,
  perform: (elements, appState, _, app) => {
    const result = alignSelectedElements(elements, appState, app, {
      position: "vertical",
      axis: "arrange",
    });

    // Scroll to content center after arrange
    // setTimeout(() => {
    //   app.scrollToContent(result.updatedElements, { animate: true, duration: 300 });
    // }, 0);
    return {
      appState: {
        ...appState,
        selectedElementIds: {},
      },
      elements: result.elements,
      commitToHistory: true,
      arrange: true,
    };
  },
  keyTest: (event) =>
      event[KEYS.CTRL_OR_CMD] && event.shiftKey && event.key === KEYS.ARROW_RIGHT,
  PanelComponent: ({ elements, appState, updateData, app }) => (
      <ToolButton
          hidden={!alignActionsPredicate(elements, appState, null, app)}
          type="button"
          // icon={AlignVerticalIcon}
          onClick={() => updateData(null)}
          title={`${t("labels.alignRight")} — ${getShortcutKey(
              "CtrlOrCmd+Shift+Right",
          )}`}
          aria-label={t("labels.alignRight")}
          visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
      />
  ),
});
export const actionAlignGrid = register({
  name: "alignGrid",
  trackEvent: { category: "element" },
  predicate: alignActionsPredicate,
  perform: (elements, appState, _, app) => {
    const result = alignSelectedElements(elements, appState, app, {
      position: "grid",
      axis: "arrange",
    });

    // Scroll to content center after arrange
    // setTimeout(() => {
    //   app.scrollToContent(result.updatedElements, { animate: true, duration: 300 });
    // }, 0);
    return {
      appState: {
        ...appState,
        selectedElementIds: {},
      },
      elements: result.elements,
      commitToHistory: true,
      arrange: true,
    };
  },
  keyTest: (event) =>
      event[KEYS.CTRL_OR_CMD] && event.shiftKey && event.key === KEYS.ARROW_RIGHT,
  PanelComponent: ({ elements, appState, updateData, app }) => (
      <ToolButton
          hidden={!alignActionsPredicate(elements, appState, null, app)}
          type="button"
          // icon={AlignnGridIcon}
          onClick={() => updateData(null)}
          title={`${t("labels.alignRight")} — ${getShortcutKey(
              "CtrlOrCmd+Shift+Right",
          )}`}
          aria-label={t("labels.alignRight")}
          visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
      />
  ),
});
